import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default CMS content (matches admin editor defaults)
const defaultContent = {
  hero: {
    title: 'Get in',
    highlight: 'Touch',
    description: 'Have questions or need assistance? We\'re here to help. Reach out to us through any of the channels below.',
  },
  form: {
    heading: 'Send us a Message',
    subheading: 'Fill out the form below and we\'ll get back to you shortly',
  },
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    fetchCmsContent();
    fetchContactInfo();
  }, []);

  const fetchCmsContent = async () => {
    try {
      const res = await axios.get(`${API}/page-content/contact`);
      const next = { ...defaultContent };
      
      res.data.forEach((item) => {
        if (item.section_name && item.content) {
          next[item.section_name] = { ...next[item.section_name], ...item.content };
        }
      });
      
      setContent(next);
    } catch (e) {
      // Use defaults
    }
  };

  const fetchContactInfo = async () => {
    try {
      const response = await axios.get(`${API}/contact-info`);
      setContactInfo(response.data);
    } catch (error) {
      console.error('Error fetching contact info:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully!', {
        description: 'We will get back to you as soon as possible.'
      });
      
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message', {
        description: 'Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section - CMS Driven */}
      <section className="bg-gradient-to-br from-orange-50 via-blue-50 to-green-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {content.hero.title} <span className="text-orange-500">{content.hero.highlight}</span>
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            {content.hero.description}
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 card-hover">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <MapPin className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Office Address</h3>
                <p className="text-gray-700 text-sm">
                  {contactInfo ? (
                    <>
                      {contactInfo.address}<br />
                      {contactInfo.city}<br />
                      {contactInfo.country}
                    </>
                  ) : (
                    <>
                      Port Moresby<br />
                      Papua New Guinea
                    </>
                  )}
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 card-hover">
                <div className="w-12 h-12 bg-blue-400 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <Phone className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Phone</h3>
                <p className="text-gray-700 text-sm">
                  {contactInfo ? contactInfo.phone : '+675 XXX XXXX'}
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 card-hover">
                <div className="w-12 h-12 bg-green-400 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <Mail className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Email</h3>
                <p className="text-gray-700 text-sm">
                  {contactInfo ? contactInfo.email : 'info@peapng.org'}
                </p>
              </div>
            </div>

            {/* Contact Form - CMS Driven */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="mb-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{content.form.heading}</h2>
                  <p className="text-gray-600">{content.form.subheading}</p>
                </div>

                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Your Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="input-name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="input-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      data-testid="input-subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="input-message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="mt-1"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="submit-contact-btn"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6 text-lg font-semibold"
                  >
                    {loading ? 'Sending...' : (
                      <>
                        <Send className="mr-2" size={20} />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
